"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Plus, RefreshCw } from "lucide-react"
import { DataTable } from "@/components/data-table/data-table"
import { DataTableFacetedFilter } from "@/components/data-table/data-table-faceted-filter"
import { getColumns } from "./data-table/columns"
import { RouteAreaRateDialog } from "./RouteAreaRateDialog"
import { RouteAreaRate } from "@/utils/routeAreaRate"
import axiosInstance from "@/utils/axiosInstance"

const RouteAreaRatesList = () => {
    // Local state for data loading
    const [data, setData] = React.useState<RouteAreaRate[]>([])
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    // Local state for dialog
    const [selectedRate, setSelectedRate] = React.useState<RouteAreaRate | null>(null)
    const [dialogOpen, setDialogOpen] = React.useState(false)
    const [dialogMode, setDialogMode] = React.useState<"create" | "edit">("create")

    // Data fetching function
    const fetchRouteAreaRates = React.useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const response = await axiosInstance.get('/route-area-rates')
            setData(response.data || [])
        } catch (err) {
            setError('Failed to load fixed rates')
            console.error('Error fetching fixed rates:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    // Load data on component mount
    React.useEffect(() => {
        fetchRouteAreaRates()
    }, [fetchRouteAreaRates])

    const handleCreate = () => {
        setSelectedRate(null)
        setDialogMode("create")
        setDialogOpen(true)
    }

    const handleRefresh = () => {
        fetchRouteAreaRates()
    }

    const handleEditClick = (rate: RouteAreaRate) => {
        setSelectedRate(rate)
        setDialogMode("edit")
        setDialogOpen(true)
    }

    const handleDeleteSuccess = () => {
        fetchRouteAreaRates()
    }

    const columns = getColumns(handleEditClick, handleDeleteSuccess)

    // Generate unique pickup area options from data
    const pickupAreaOptions = React.useMemo(() => {
        const uniquePickupAreas = new Map()
        data.forEach(rate => {
            if (!uniquePickupAreas.has(rate.pickup_area.id)) {
                uniquePickupAreas.set(rate.pickup_area.id, {
                    label: rate.pickup_area.name,
                    value: rate.pickup_area.name,
                })
            }
        })
        return Array.from(uniquePickupAreas.values())
    }, [data])

    // Generate unique dropoff area options from data
    const dropoffAreaOptions = React.useMemo(() => {
        const uniqueDropoffAreas = new Map()
        data.forEach(rate => {
            if (!uniqueDropoffAreas.has(rate.dropoff_area.id)) {
                uniqueDropoffAreas.set(rate.dropoff_area.id, {
                    label: rate.dropoff_area.name,
                    value: rate.dropoff_area.name,
                })
            }
        })
        return Array.from(uniqueDropoffAreas.values())
    }, [data])

    const renderToolbarFilters = (table: any) => (
        <div className="flex items-center space-x-2">
            {table?.getColumn("pickup_area") && (
                <DataTableFacetedFilter
                    column={table.getColumn("pickup_area")}
                    title="Pickup Area"
                    options={pickupAreaOptions}
                />
            )}
            {table?.getColumn("dropoff_area") && (
                <DataTableFacetedFilter
                    column={table.getColumn("dropoff_area")}
                    title="Dropoff Area"
                    options={dropoffAreaOptions}
                />
            )}
        </div>
    )

    return (
        <div className="space-y-4">
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600">{error}</p>
                </div>
            )}

            <div className="p-6">
                <DataTable
                    data={data}
                    columns={columns}
                    renderToolbarFilters={renderToolbarFilters}
                    leftActions={
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            className="h-8"
                            disabled={loading}
                        >
                            <RefreshCw className={`${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    }
                    rightActions={
                        <Button
                            size="sm"
                            onClick={handleCreate}
                            className="h-8"
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Fixed Rate
                        </Button>
                    }
                />

                <RouteAreaRateDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    rate={selectedRate}
                    mode={dialogMode}
                    onSuccess={() => {
                        setDialogOpen(false)
                        fetchRouteAreaRates()
                    }}
                />
            </div>
        </div>
    )
}

export default RouteAreaRatesList